import * as moment from 'moment'

import { ActionContext, Module } from 'vuex'
import { OnboardingState, RootState } from '../store/state_models'

import { AuthUser } from '../models'
import { IUpdateStripeCustomer } from '../types/customer'
import InternalToolsService from '../services/InternalToolsService'
import { OnboardingService } from '../services'
import { USER_ROLE } from '../models/user'
import { aiEmployeePromoRoutes } from '../routes/constants/agency'
import { conversationAIRouteNames } from '../routes/constants/location'

const summerOfAIPromoFunnels = [
  // staging
  'qPJuzvENA7bwH7gAekc1',
  'lPJuzvENA7bwH7gAekc2',
  // production
  'ISbJmjFkPujrmZy2su17',
]

const isActiveStatus = (companyStatus: string) => {
  const allowedStatus = ['active', 'active_trial', 'active_trial-extension']
  return allowedStatus.includes(companyStatus)
}

const isAdmin = (role: string) => {
  return role === USER_ROLE.ADMIN
}

let unsubscribe: () => void

export const onboarding: Module<OnboardingState, RootState> = {
  namespaced: true,
  state: {
    onboardingInfo: {},
    emailVerified: false,
    phoneNumberVerified: false,
    widgetState: false,
    version: null,
    widgetPosition: {
      x: null,
      y: null,
    },
    startValuePropTourGuide: false,
    showSummerOfAIMenu: undefined
  },
  mutations: {
    set: (state: { version: number }, payload: { version?: number }) => {
      if (payload.version) {
        state.version = payload.version
      }
    },
	setWidgetPosition: (state: { widgetPosition: object }, payload: { x?: number, y?: number }) => {
		state.widgetPosition.x = payload.x
		state.widgetPosition.y = payload.y
  },
  setOnboardingInfo: (state: { onboardingInfo: object }, payload: {}) => {
		state.onboardingInfo = payload
	  },
    setStartValuePropTourGuide: (state: { startValuePropTourGuide: boolean }, payload: { startValuePropTourGuide: boolean }) => {
      state.startValuePropTourGuide = payload.startValuePropTourGuide
    },
    setShowSummerOfAIMenu(state, payload: boolean) {
      state.showSummerOfAIMenu = payload
    }
  },
  actions: {
    verifyStatus: {
      handler: (context: ActionContext<OnboardingState, RootState>): Promise<OnboardingState> => {
        return new Promise(async (resolve, reject) => {
          try {
            let credentials = new AuthUser(await context.dispatch('auth/get', null, { root: true }))
            const data = await OnboardingService.verifyStatus(credentials.companyId)
            resolve({
              emailVerified: data.emailVerified || false,
              phoneNumberVerified: data.phoneNumberVerified || false,
            })
          } catch (e) {
            resolve({ emailVerified: false, phoneNumberVerified: false })
          }
        })
      },
    },
    updateStripeCustomer: {
      handler: (context: ActionContext<OnboardingState, RootState>, body: IUpdateStripeCustomer): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            const data = await OnboardingService.updateStripeCustomer(body)
            resolve(data)
          } catch (e) {
            reject(e)
          }
        })
      },
    },
    getTrialDaysLeft: {
      handler: (context: ActionContext<OnboardingState, RootState>, companyId: string): Promise<number> => {
        return new Promise(async resolve => {
          try {
            const { status, subscription } = await InternalToolsService.getCompanyBillingPlan(companyId)
            if (status.startsWith('trialing')) {
              let trialEnd = moment.unix(subscription.trialEnd)
              let daysLeft = trialEnd.diff(moment(), 'days')
              resolve(daysLeft)
            }

            resolve(0)
          } catch (e) {
            console.warn('Error getting trial days left', e)
            resolve(0)
          }
        })
      },
    },
    widgetState: {
      handler: (context: ActionContext<OnboardingState, RootState>, state: Boolean): void => {
        // context.state.widgetState = state
        context.commit('set', { widgetState: state })
      },
    },
    getWidgetState: {
      handler: (context: ActionContext<OnboardingState, RootState>): Boolean => {
        return context.state.widgetState
      },
    },
    getWidgetPosition: {
      handler: (context: ActionContext<OnboardingState, RootState>): Boolean => {
        return context.state.widgetPosition
      },
    },
    setWidgetPosition: {
      handler: (context: ActionContext<OnboardingState, RootState>, widgetPosition: object): void => {
        context.commit('setWidgetPosition', { x: widgetPosition?.x, y: widgetPosition?.y })
      },
    },
    getOnboardingVersion: {
      handler: (
        context: ActionContext<OnboardingState, RootState>,
        { companyId }
      ): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            if (context.state.version) {
              return resolve(context.state.version)
            }
            const data = await OnboardingService.getVersion({ companyId })
            if (data) {
              context.commit('set', { version: data })
              resolve(data)
            } else {
              resolve(null)
            }
          } catch (e) {
            resolve(null)
          }
        })
      },
    },
    getOnboardingInfo: {
      handler: (
        context: ActionContext<OnboardingState, RootState>,
        { companyId, forceRefresh = false }
      ): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            // Return cached data only if forceRefresh is false and we have data
            if (!forceRefresh && Object.keys(context.state.onboardingInfo).length > 0) {
              return resolve(context.state.onboardingInfo)
            }
            const data = await OnboardingService.getInfo({ companyId })
            if (data) {
              context.commit('setOnboardingInfo', data)
              resolve(data)
            } else {
              resolve(null)
            }
          } catch (e) {
            resolve(null)
          }
        })
      },
    },
    checkAndSetSummerOfAI: {
      handler: async (
        context: ActionContext<OnboardingState, RootState>,
        { locationId, companyId, companyStatus, userRole }: { locationId?: string; companyId?: string; companyStatus?: string; userRole?: string }
      ): Promise<boolean> => {
        try {
          let showSummerOfAIMenu = undefined

          if (locationId) {
            if (context.state.showSummerOfAIMenu === undefined) {
              showSummerOfAIMenu = await context.dispatch('aiLocationPlan/fetchShowSummerOfAIMenu', { locationId }, { root: true })
            } 
          }

          if (!locationId) {
            const onboardingInfo = await context.dispatch('getOnboardingInfo', { companyId })

            // if user is admin and company is paid, trial, extended trial, show summer of ai menu
            // for existing agencies as well.
            if (
              isActiveStatus(companyStatus) &&
              isAdmin(userRole)
            ) {
              showSummerOfAIMenu = true
            }

            // If funnelId is found, check if it is a summer of ai funnel
            let funnelId = onboardingInfo?.funnelId || null;
            if(funnelId) {
              if(summerOfAIPromoFunnels.includes(funnelId)) {
                showSummerOfAIMenu = true
              } else {
                showSummerOfAIMenu = false
              }
            } 
          }

          // context.commit('setShowSummerOfAIMenu', showSummerOfAIMenu)
          return showSummerOfAIMenu
        } catch (error) {
          console.error('Error in checkAndSetSummerOfAI:', error)
          return false
        }
      }
    }
  },
}
